import React, { useEffect, useMemo, useState, useRef } from "react";
import Aside from "../components/Aside";

import { DayPicker } from "react-day-picker";
import { ptBR } from "date-fns/locale";
import { format } from "date-fns";
import "react-day-picker/dist/style.css";

import ResumoOcupacaoChart from "../components/ResumoOcupacaoChart";

import { FaCalendarAlt, FaCheckCircle, FaInfoCircle } from "react-icons/fa";
import { MdCancel, MdEdit } from "react-icons/md";

import {
  Alert,
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Paper,
  Snackbar,
  Stack,
  Typography,
  useMediaQuery,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  OutlinedInput,
} from "@mui/material";

import { database } from "../services/firebase";
import { ref, onValue, set, get, remove } from "firebase/database";

// ==============================
// STYLES & CONSTANTS
// ==============================
const CARD_STYLE = {
  border: "1px solid rgba(0,0,0,0.08)",
  borderRadius: 4,
  p: 3,
  background: "#fff",
  height: "100%",
  width: "100%",
  display: "flex",
  flexDirection: "column",
  boxShadow: "0px 4px 20px rgba(0,0,0,0.03)",
};

const Dashboard = () => {
  const theme = useTheme();
  const CAP_SALAO = 44;
  const CAP_DECK = 16;

  const mesasDeck = useMemo(
    () => [100, 200, 300, 400, 500, 600, 800, 900, 1000, 1001],
    []
  );

  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const ASIDE_W = 260;
  const MOBILE_TOPBAR_H = 64;

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [pendentes, setPendentes] = useState([]);
  const [confirmadasDoDia, setConfirmadasDoDia] = useState([]);

  // Dialogs & UI States
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [reservaParaCancelar, setReservaParaCancelar] = useState(null);
  const [confirmPagamentoOpen, setConfirmPagamentoOpen] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState("");
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [reservaDetalhe, setReservaDetalhe] = useState(null);

  // Edit States
  const [editOpen, setEditOpen] = useState(false);
  const [editReserva, setEditReserva] = useState(null);
  const [editForm, setEditForm] = useState({
    primeiroNome: "",
    sobrenome: "",
    telefone: "",
    pessoas: "",
    horario: "",
    local: "Salão",
    periodo: "almoco",
    data: "",
  });
  const [editCalendarOpen, setEditCalendarOpen] = useState(false);
  const editDatepickerRef = useRef(null);
  const [capacidadeRestanteEdit, setCapacidadeRestanteEdit] = useState(null);
  const [diasConfig, setDiasConfig] = useState({});
  const [almocoDisponivelEdit, setAlmocoDisponivelEdit] = useState(true);
  const [jantarDisponivelEdit, setJantarDisponivelEdit] = useState(true);
  const [editMesasSelecionadas, setEditMesasSelecionadas] = useState([]);
  const [editMesasOcupadas, setEditMesasOcupadas] = useState([]);

  // Allocation States
  const [dialogOpen, setDialogOpen] = useState(false);
  const [reservaSelecionada, setReservaSelecionada] = useState(null);
  const [mesasSelecionadas, setMesasSelecionadas] = useState([]);
  const [mesasOcupadas, setMesasOcupadas] = useState([]);

  const lunchTimes = ["13:15", "13:30", "14:00"];
  const dinnerTimes = ["19:15", "19:30", "20:00"];

  const availableTimesEdit =
    editForm.periodo === "almoco"
      ? lunchTimes
      : editForm.periodo === "jantar"
        ? dinnerTimes
        : [];

  const [resumoDia, setResumoDia] = useState({
    almoco: {
      confirmadas: 0,
      lugaresDisponiveis: 0,
      mesasDeckDisponiveis: 0,
      mesasSalaoDisponiveis: 0,
      valorTotal: 0,
    },
    jantar: {
      confirmadas: 0,
      lugaresDisponiveis: 0,
      mesasDeckDisponiveis: 0,
      mesasSalaoDisponiveis: 0,
      valorTotal: 0,
    },
    totalDia: 0,
  });

  const formatDateKey = (date) => format(date, "yyyy-MM-dd");
  const formattedDate = format(selectedDate, "dd/MM/yyyy");
  const formatDateBR = (str) => {
    if (!str) return "-";
    const [y, m, d] = str.split("-");
    return `${d}/${m}/${y}`;
  };

  const showSnack = (msg) => {
    setSnackMsg(msg);
    setSnackOpen(true);
  };

  const abrirDetalhes = (r) => {
    setReservaDetalhe(r);
    setDetailsOpen(true);
  };

  // ==============================
  // FIREBASE LISTENERS & LOGIC
  // ==============================

  useEffect(() => {
    const diasRef = ref(database, "config/dias");
    const unsub = onValue(diasRef, (snap) => {
      setDiasConfig(snap.exists() ? snap.val() : {});
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!editForm.data) {
      setAlmocoDisponivelEdit(true);
      setJantarDisponivelEdit(true);
      return;
    }
    const config = diasConfig[editForm.data];
    if (!config) {
      setAlmocoDisponivelEdit(true);
      setJantarDisponivelEdit(true);
      return;
    }
    setAlmocoDisponivelEdit(config.almoco === true);
    setJantarDisponivelEdit(config.jantar === true);

    if (
      (editForm.periodo === "almoco" && config.almoco !== true) ||
      (editForm.periodo === "jantar" && config.jantar !== true)
    ) {
      onEditChange("periodo", "");
      onEditChange("horario", "");
    }
  }, [editForm.data, diasConfig, editForm.periodo]); // Added editForm.periodo to dependency

  // Click Outside Calendar Edit
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!editCalendarOpen) return;
      if (event.target.closest(".datepicker-wrapper")) return;
      if (
        editDatepickerRef.current &&
        !editDatepickerRef.current.contains(event.target)
      ) {
        setEditCalendarOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [editCalendarOpen]);

  // Update Capacity Edit
  useEffect(() => {
    async function atualizarCapacidadeEdit() {
      const { data, periodo, local } = editForm;
      if (!data || !periodo || !local || !editReserva) {
        setCapacidadeRestanteEdit(null);
        return;
      }
      const localKey = local === "Salão" ? "salao" : "deck";
      const capacidadeTotal = localKey === "salao" ? CAP_SALAO : CAP_DECK;
      const usadaAtual = await getCapacidadeUsada(data, periodo, localKey);
      const usadaSemEssa = usadaAtual - Number(editReserva.pessoas);
      setCapacidadeRestanteEdit(capacidadeTotal - usadaSemEssa);
    }
    atualizarCapacidadeEdit();
  }, [editForm.data, editForm.periodo, editForm.local, editReserva]);

  // Load Pendentes
  useEffect(() => {
    const reservasRef = ref(database, "reservas");
    const unsubscribe = onValue(reservasRef, (snap) => {
      const out = [];
      if (snap.exists()) {
        Object.entries(snap.val()).forEach(([dateKey, periodos]) => {
          ["almoco", "jantar"].forEach((periodo) => {
            if (periodos?.[periodo]) {
              Object.entries(periodos[periodo]).forEach(([id, r]) => {
                if (r.status === "pendente") out.push({ id, ...r });
              });
            }
          });
        });
      }
      setPendentes(out);
    });
    return () => unsubscribe();
  }, []);

  // Load Confirmadas do Dia
  useEffect(() => {
    const dateKey = formatDateKey(selectedDate);
    const refDia = ref(database, `reservas/${dateKey}`);
    const unsubscribe = onValue(refDia, (snap) => {
      if (!snap.exists()) {
        setConfirmadasDoDia([]);
        return;
      }
      const list = [];
      const data = snap.val();
      ["almoco", "jantar"].forEach((p) => {
        if (data?.[p]) {
          Object.entries(data[p]).forEach(([id, r]) => {
            if (r.status === "confirmada") list.push({ id, ...r });
          });
        }
      });
      setConfirmadasDoDia(list);
    });
    return () => unsubscribe();
  }, [selectedDate]);

  // Calcular Resumo
  const calcularResumo = async () => {
    const dataKey = formatDateKey(selectedDate);
    const almoco = confirmadasDoDia.filter((r) => r.periodo === "almoco");
    const jantar = confirmadasDoDia.filter((r) => r.periodo === "jantar");

    const usadasAlmocoSalao = await getCapacidadeUsada(
      dataKey,
      "almoco",
      "salao"
    );
    const usadasAlmocoDeck = await getCapacidadeUsada(
      dataKey,
      "almoco",
      "deck"
    );
    const usadasJantarSalao = await getCapacidadeUsada(
      dataKey,
      "jantar",
      "salao"
    );
    const usadasJantarDeck = await getCapacidadeUsada(
      dataKey,
      "jantar",
      "deck"
    );

    const pessoasAlmoco = usadasAlmocoSalao + usadasAlmocoDeck;
    const pessoasJantar = usadasJantarSalao + usadasJantarDeck;

    const valorAlmoco = pessoasAlmoco * 50;
    const valorJantar = pessoasJantar * 50;

    setResumoDia({
      almoco: {
        confirmadas: almoco.length,
        lugaresDisponiveis: CAP_SALAO + CAP_DECK - pessoasAlmoco,
        mesasDeckDisponiveis:
          mesasDeck.length - almoco.flatMap((r) => r.mesas || []).length,
        mesasSalaoDisponiveis:
          17 -
          almoco
            .filter((r) => r.local === "Salão")
            .flatMap((r) => r.mesas || []).length,
        valorTotal: valorAlmoco,
      },
      jantar: {
        confirmadas: jantar.length,
        lugaresDisponiveis: CAP_SALAO + CAP_DECK - pessoasJantar,
        mesasDeckDisponiveis:
          mesasDeck.length - jantar.flatMap((r) => r.mesas || []).length,
        mesasSalaoDisponiveis:
          17 -
          jantar
            .filter((r) => r.local === "Salão")
            .flatMap((r) => r.mesas || []).length,
        valorTotal: valorJantar,
      },
      totalDia: valorAlmoco + valorJantar,
    });
  };

  useEffect(() => {
    calcularResumo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirmadasDoDia]);

  // ==============================
  // ACTIONS & HANDLERS
  // ==============================

  const abrirDialogAlocacao = async (reserva) => {
    setReservaSelecionada(reserva);
    setMesasSelecionadas([]);
    const reservasRef = ref(
      database,
      `reservas/${reserva.data}/${reserva.periodo}`
    );
    const snap = await get(reservasRef);
    let ocupadas = [];
    if (snap.exists()) {
      Object.values(snap.val()).forEach((r) => {
        if (r.status === "confirmada" && r.local === reserva.local && r.mesas) {
          ocupadas = [...ocupadas, ...r.mesas];
        }
      });
    }
    setMesasOcupadas(ocupadas);
    setDialogOpen(true);
  };

  const fecharDialog = () => setDialogOpen(false);

  const toggleMesa = (mesa) => {
    setMesasSelecionadas((prev) =>
      prev.includes(mesa) ? prev.filter((m) => m !== mesa) : [...prev, mesa]
    );
  };

  const confirmarAlocacaoMesas = async () => {
    if (mesasSelecionadas.length === 0) {
      showSnack("Selecione ao menos uma mesa.");
      return;
    }
    const r = reservaSelecionada;
    await set(ref(database, `reservas/${r.data}/${r.periodo}/${r.id}`), {
      ...r,
      status: "confirmada",
      mesas: mesasSelecionadas,
    });
    await set(
      ref(database, `reservas_alocacao/${r.data}/${r.periodo}/${r.id}`),
      {
        mesas: mesasSelecionadas,
      }
    );
    setDialogOpen(false);
  };

  const cancelarReserva = async (r) => {
    const localKey = r.local === "Salão" ? "salao" : "deck";
    const usoRef = ref(
      database,
      `reservas_uso/${r.data}/${r.periodo}/${localKey}/capacidade_usada`
    );
    const snap = await get(usoRef);
    const atual = snap.exists() ? snap.val() : 0;
    await set(usoRef, atual - Number(r.pessoas));
    await remove(ref(database, `reservas/${r.data}/${r.periodo}/${r.id}`));
    await remove(
      ref(database, `reservas_alocacao/${r.data}/${r.periodo}/${r.id}`)
    );
  };

  // Edit Logic
  const prevContextRef = useRef({ data: null, periodo: null, local: null });

  const abrirEdicao = async (r) => {
    setEditReserva(r);
    setEditForm({
      primeiroNome: r.primeiroNome || "",
      sobrenome: r.sobrenome || "",
      telefone: r.telefone || "",
      pessoas: r.pessoas ?? "",
      horario: r.horario || "",
      local: r.local || "Salão",
      periodo: r.periodo || "almoco",
      data: r.data || "",
    });
    setEditMesasSelecionadas(r.mesas || []);

    const reservasRef = ref(database, `reservas/${r.data}/${r.periodo}`);
    const snap = await get(reservasRef);
    let ocupadas = [];
    if (snap.exists()) {
      Object.entries(snap.val()).forEach(([id, reserva]) => {
        if (
          reserva.status === "confirmada" &&
          reserva.local === r.local &&
          reserva.mesas &&
          id !== r.id
        ) {
          ocupadas.push(...reserva.mesas);
        }
      });
    }
    setEditMesasOcupadas(ocupadas);
    setEditOpen(true);
  };

  useEffect(() => {
    if (!editReserva) return;
    const prev = prevContextRef.current;
    const mudouContexto =
      prev.data &&
      (prev.data !== editForm.data ||
        prev.periodo !== editForm.periodo ||
        prev.local !== editForm.local);

    if (mudouContexto) {
      setEditMesasSelecionadas([]);
    }
    prevContextRef.current = {
      data: editForm.data,
      periodo: editForm.periodo,
      local: editForm.local,
    };

    async function reloadMesas() {
      const reservasRef = ref(
        database,
        `reservas/${editForm.data}/${editForm.periodo}`
      );
      const snap = await get(reservasRef);
      let ocupadas = [];
      if (snap.exists()) {
        Object.entries(snap.val()).forEach(([id, r]) => {
          if (
            r.status === "confirmada" &&
            r.local === editForm.local &&
            r.mesas &&
            id !== editReserva.id
          ) {
            ocupadas.push(...r.mesas);
          }
        });
      }
      setEditMesasOcupadas(ocupadas);
    }
    if (editForm.data && editForm.periodo && editForm.local) {
      reloadMesas();
    }
  }, [editForm.data, editForm.periodo, editForm.local, editReserva]);

  const toggleEditMesa = (mesa) => {
    setEditMesasSelecionadas((prev) =>
      prev.includes(mesa) ? prev.filter((m) => m !== mesa) : [...prev, mesa]
    );
  };

  const onEditChange = (key, value) => {
    setEditForm((prev) => ({ ...prev, [key]: value }));
  };

  const isDayDisabled = (day) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (day < today) return true;
    const key = format(day, "yyyy-MM-dd");
    const config = diasConfig[key];
    if (config) {
      return !(config.almoco === true || config.jantar === true);
    }
    if (day.getDay() === 1) return true;
    return false;
  };

  const parseDateLocal = (dateStr) => {
    if (!dateStr) return undefined;
    const [y, m, d] = dateStr.split("-").map(Number);
    return new Date(y, m - 1, d);
  };

  const isTimeAvailable = (selectedDate, selectedPeriod, selectedTime) => {
    if (!selectedDate || !selectedPeriod || !selectedTime) return true;
    const now = new Date();
    const isToday =
      selectedDate.getDate() === now.getDate() &&
      selectedDate.getMonth() === now.getMonth() &&
      selectedDate.getFullYear() === now.getFullYear();
    if (!isToday) return true;
    const [h, m] = selectedTime.split(":").map(Number);
    const selectedDateTime = new Date(
      selectedDate.getFullYear(),
      selectedDate.getMonth(),
      selectedDate.getDate(),
      h,
      m,
      0
    );
    const diffMin = (selectedDateTime - now) / 1000 / 60;
    if (diffMin < 60) return false;
    if (now >= selectedDateTime) return false;
    return true;
  };

  const getCapacidadeUsada = async (data, periodo, localKey) => {
    const usoRef = ref(
      database,
      `reservas_uso/${data}/${periodo}/${localKey}/capacidade_usada`
    );
    const snap = await get(usoRef);
    return snap.exists() ? snap.val() : 0;
  };

  const salvarEdicaoReserva = async () => {
    try {
      const rAntiga = editReserva;
      const f = editForm;

      if (
        !f.primeiroNome ||
        !f.sobrenome ||
        !f.telefone ||
        !f.data ||
        !f.periodo ||
        !f.local ||
        !f.horario ||
        !f.pessoas
      ) {
        showSnack("Preencha todos os campos.");
        return;
      }
      const phoneRegex = /^\(\d{2}\) \d{5}-\d{4}$/;
      if (!phoneRegex.test(f.telefone)) {
        showSnack("Telefone inválido.");
        return;
      }
      const dateObj = new Date(f.data + "T00:00:00");
      if (!isTimeAvailable(dateObj, f.periodo, f.horario)) {
        showSnack("Horário inválido para essa data.");
        return;
      }
      if (editMesasSelecionadas.length === 0) {
        showSnack("Selecione ao menos uma mesa.");
        return;
      }

      const oldPath = `reservas/${rAntiga.data}/${rAntiga.periodo}/${rAntiga.id}`;
      const newPath = `reservas/${f.data}/${f.periodo}/${rAntiga.id}`;
      const oldLocalKey = rAntiga.local === "Salão" ? "salao" : "deck";
      const newLocalKey = f.local === "Salão" ? "salao" : "deck";
      const capacidadeTotal =
        newLocalKey === "salao" ? CAP_SALAO : CAP_DECK;

      const capacidadeUsadaNova = await getCapacidadeUsada(
        f.data,
        f.periodo,
        newLocalKey
      );
      const mesmaChave =
        rAntiga.data === f.data &&
        rAntiga.periodo === f.periodo &&
        oldLocalKey === newLocalKey;
      const capacidadeBase = mesmaChave
        ? capacidadeUsadaNova - Number(rAntiga.pessoas)
        : capacidadeUsadaNova;

      if (capacidadeBase + Number(f.pessoas) > capacidadeTotal) {
        showSnack("Não há capacidade para essa alteração.");
        return;
      }

      const usoOldRef = ref(
        database,
        `reservas_uso/${rAntiga.data}/${rAntiga.periodo}/${oldLocalKey}/capacidade_usada`
      );
      const snapOld = await get(usoOldRef);
      const oldUsed = snapOld.exists() ? snapOld.val() : 0;
      await set(usoOldRef, oldUsed - Number(rAntiga.pessoas));

      const usoNewRef = ref(
        database,
        `reservas_uso/${f.data}/${f.periodo}/${newLocalKey}/capacidade_usada`
      );
      await set(usoNewRef, capacidadeBase + Number(f.pessoas));

      await remove(ref(database, oldPath));
      await remove(
        ref(
          database,
          `reservas_alocacao/${rAntiga.data}/${rAntiga.periodo}/${rAntiga.id}`
        )
      );

      await set(ref(database, newPath), {
        ...rAntiga,
        ...f,
        pessoas: Number(f.pessoas),
        status: rAntiga.status,
        mesas: editMesasSelecionadas,
      });

      await set(
        ref(database, `reservas_alocacao/${f.data}/${f.periodo}/${rAntiga.id}`),
        { mesas: editMesasSelecionadas }
      );

      setEditOpen(false);
      showSnack("Reserva atualizada com sucesso.");
    } catch (err) {
      console.error(err);
      showSnack("Erro ao salvar edição.");
    }
  };

  // ==============================
  // SUB-COMPONENTS
  // ==============================

  const ReservationTable = ({ title, data, variant }) => {
    const isPendentes = variant === "pendente";

    return (
      <Paper elevation={0} sx={{ ...CARD_STYLE, p: 0, overflow: "hidden" }}>
        <Box
          sx={{
            px: 3,
            py: 2,
            borderBottom: "1px solid rgba(0,0,0,0.06)",
            bgcolor: isPendentes ? "rgba(255, 152, 0, 0.05)" : "transparent",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 800,
              color: isPendentes ? "var(--laranja)" : "var(--azulEscuro)",
              letterSpacing: -0.5,
            }}
          >
            {title}
          </Typography>
        </Box>

        <TableContainer sx={{ width: "100%", overflowX: "auto" }}>
          <Table sx={{ minWidth: 980 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: "#fafafa" }}>
                {[
                  "Cliente",
                  "Data",
                  "Período",
                  "Local",
                  "Horário",
                  "Pessoas",
                  "Mesas",
                  "Telefone",
                  "Status",
                  "Ações",
                ].map((head) => (
                  <TableCell
                    key={head}
                    sx={{
                      fontWeight: 800,
                      color: "var(--text-muted)",
                      textTransform: "uppercase",
                      fontSize: "0.75rem",
                      letterSpacing: 0.5,
                    }}
                    align={head === "Ações" ? "center" : "left"}
                  >
                    {head}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {data.map((r) => {
                const statusLabel =
                  r.status === "confirmada" ? "Confirmada" : "Pendente";

                return (
                  <TableRow key={r.id} hover>
                    <TableCell>
                      <Typography
                        sx={{ fontWeight: 700, color: "var(--azulEscuro)" }}
                      >
                        {r.primeiroNome} {r.sobrenome}
                      </Typography>
                    </TableCell>

                    <TableCell sx={{ color: "var(--cinza)", fontWeight: 500 }}>
                      {formatDateBR(r.data)}
                    </TableCell>

                    <TableCell>
                      <Chip
                        size="small"
                        label={r.periodo === "almoco" ? "ALMOÇO" : "JANTAR"}
                        sx={{
                          fontWeight: 800,
                          bgcolor:
                            r.periodo === "almoco"
                              ? "rgba(7,23,57,0.08)"
                              : "rgba(0,0,0,0.8)",
                          color: r.periodo === "almoco" ? "var(--azulEscuro)" : "#fff",
                          fontSize: "0.7rem",
                          borderRadius: 1,
                        }}
                      />
                    </TableCell>

                    <TableCell sx={{ fontWeight: 600 }}>{r.local}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{r.horario}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{r.pessoas}</TableCell>

                    <TableCell sx={{ color: "var(--cinza)" }}>
                      {r.mesas?.length ? r.mesas.join(", ") : "-"}
                    </TableCell>

                    <TableCell sx={{ color: "var(--cinza)" }}>
                      {r.telefone || "-"}
                    </TableCell>

                    <TableCell>
                      <Chip
                        size="small"
                        label={statusLabel}
                        color={
                          r.status === "confirmada" ? "success" : "warning"
                        }
                        variant={
                          r.status === "confirmada" ? "filled" : "outlined"
                        }
                        sx={{ fontWeight: 800 }}
                      />
                    </TableCell>

                    <TableCell align="center">
                      <Stack
                        direction="row"
                        justifyContent="center"
                        spacing={1}
                      >
                        <Tooltip title="Detalhes">
                          <IconButton
                            size="small"
                            onClick={() => abrirDetalhes(r)}
                          >
                            <FaInfoCircle color="var(--cinza)" />
                          </IconButton>
                        </Tooltip>

                        {isPendentes && (
                          <Tooltip title="Confirmar">
                            <IconButton
                              size="small"
                              onClick={() => abrirDialogAlocacao(r)}
                            >
                              <FaCheckCircle color="var(--azulEscuro)" />
                            </IconButton>
                          </Tooltip>
                        )}

                        {r.status !== "pendente" && (
                          <Tooltip title="Editar">
                            <IconButton
                              size="small"
                              onClick={() => abrirEdicao(r)}
                            >
                              <MdEdit color="var(--azulEscuro)" />
                            </IconButton>
                          </Tooltip>
                        )}

                        <Tooltip title="Cancelar">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setReservaParaCancelar(r);
                              setCancelDialogOpen(true);
                            }}
                          >
                            <MdCancel color="var(--vermelho)" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}

              {data.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                    <Typography
                      sx={{ color: "var(--text-muted)", fontWeight: 600 }}
                    >
                      Nenhum registro encontrado.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    );
  };

  // ==============================
  // MAIN RENDER
  // ==============================
  return (
    <>
      <Aside />

      <Box
        component="main"
        sx={{
          minHeight: "100vh",
          background: "var(--offwhite)", // ou #f4f6f8
          ml: isMobile ? 0 : `${ASIDE_W}px`,
          pt: isMobile ? `${MOBILE_TOPBAR_H + 24}px` : 4,
          pb: 6,
          px: { xs: 2, md: 4 },
        }}
      >
        <Container maxWidth="xl" disableGutters>
          {/* HEADER SECTION */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h4" sx={{ fontWeight: 900, color: "var(--azulEscuro)", letterSpacing: -1, alignItems: "left", display: "flex" }}>
              Dashboard
            </Typography>
            <Typography sx={{ color: "var(--text-muted)", fontWeight: 500, alignItems: "left", display: "flex" }}>
              Visão geral da ocupação e reservas do dia.
            </Typography>
          </Box>

          {/* TOP METRICS GRID */}

          {/* 1. CALENDÁRIO */}

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",        // mobile
                sm: "1fr",        // mobile grande
                md: "repeat(2, 1fr)", // tablet
                lg: "repeat(3, 1fr)", // desktop
              },
              gap: 3,
              width: "100%",
              alignItems: "stretch",
              height: {
                lg: 380, // só desktop trava altura
              },
            }}
          >
            {/* 1. CALENDÁRIO */}
            <Paper sx={CARD_STYLE}>
              <Typography sx={{ fontWeight: 800, color: "var(--azulEscuro)", mb: 2 }}>
                Seletor de Data
              </Typography>

              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  justifyContent: "center",
                  "& .rdp": { m: 0 },
                }}
              >
                <DayPicker
                  mode="single"
                  locale={ptBR}
                  selected={selectedDate}
                  onSelect={(date) => {
                    if (!date) return;
                    setSelectedDate(date);
                  }}
                  disabled={isDayDisabled}
                />
              </Box>
            </Paper>

            {/* 2. RESUMO */}
            <Paper sx={CARD_STYLE}>
              <Stack direction="row" justifyContent="space-between" mb={2}>
                <Typography variant="h5" sx={{ fontWeight: 900 }}>
                  {formattedDate}
                </Typography>
                <Chip
                  label={`R$ ${resumoDia.totalDia.toFixed(2)}`}
                  sx={{ fontWeight: 900 }}
                />
              </Stack>

              <Grid container spacing={2} sx={{ flex: 1, display: "flex", justifyContent: "space-around", alignItems: "stretch" }}>
                <Grid item xs={6}>
                  <Box sx={{
                    p: 2,
                    borderRadius: 3,
                    bgcolor: "#FAFAFA",
                    border: "1px solid #EEEEEE",
                    height: "100%",
                    display: "flex", flexDirection: "column", justifyContent: "space-between"
                  }}>
                    <Typography sx={{ fontWeight: 800, color: "var(--azulEscuro)", mb: 1, textTransform: "uppercase", fontSize: "0.85rem", letterSpacing: 1 }}>
                      Almoço
                    </Typography>
                    <Stack spacing={0.5}>
                      <Typography variant="body2" color="text.secondary">Confirmadas: <Box component="span" fontWeight={800} color="text.primary">{resumoDia.almoco.confirmadas}</Box></Typography>
                      <Typography variant="body2" color="text.secondary">Vagas: <Box component="span" fontWeight={800} color="text.primary">{resumoDia.almoco.lugaresDisponiveis}</Box></Typography>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="caption" display="block" color="text.secondary">
                        Mesas Deck: <b>{resumoDia.almoco.mesasDeckDisponiveis}</b>
                      </Typography>
                      <Typography variant="caption" display="block" color="text.secondary">
                        Mesas Salão: <b>{resumoDia.almoco.mesasSalaoDisponiveis}</b>
                      </Typography>
                    </Stack>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{
                    p: 2,
                    borderRadius: 3,
                    bgcolor: "var(--azulEscuro)",
                    color: "#fff",
                    height: "100%",
                    display: "flex", flexDirection: "column", justifyContent: "space-between",
                    boxShadow: "0 4px 12px rgba(7,23,57, 0.15)"
                  }}>
                    <Typography sx={{ fontWeight: 800, mb: 1, textTransform: "uppercase", fontSize: "0.85rem", letterSpacing: 1, opacity: 0.9 }}>
                      Jantar
                    </Typography>
                    <Stack spacing={0.5}>
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>Confirmadas: <Box component="span" fontWeight={800} sx={{ opacity: 1 }}>{resumoDia.jantar.confirmadas}</Box></Typography>
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>Vagas: <Box component="span" fontWeight={800} sx={{ opacity: 1 }}>{resumoDia.jantar.lugaresDisponiveis}</Box></Typography>
                      <Divider sx={{ my: 1, borderColor: "rgba(255,255,255,0.2)" }} />
                      <Typography variant="caption" display="block" sx={{ opacity: 0.7 }}>
                        Mesas Deck: <b>{resumoDia.jantar.mesasDeckDisponiveis}</b>
                      </Typography>
                      <Typography variant="caption" display="block" sx={{ opacity: 0.7 }}>
                        Mesas Salão: <b>{resumoDia.jantar.mesasSalaoDisponiveis}</b>
                      </Typography>
                    </Stack>
                  </Box>
                </Grid>
              </Grid>
            </Paper>

            {/* 3. GRÁFICO */}
            <Paper sx={CARD_STYLE}>
              <Typography sx={{ fontWeight: 800, mb: 2 }}>
                Ocupação Visual
              </Typography>

              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <ResumoOcupacaoChart resumo={resumoDia} />
              </Box>
            </Paper>
          </Box>


          {/* TABLES SECTION */}
          <Stack spacing={4} sx={{ mt: 5 }}>
            <Box>
              <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                <FaCalendarAlt size={20} color="var(--azulEscuro)" />
                <Typography variant="h5" sx={{ fontWeight: 800, color: "var(--azulEscuro)" }}>
                  Reservas Confirmadas
                </Typography>
              </Stack>
              <ReservationTable
                title={`Lista de ${formattedDate}`}
                data={confirmadasDoDia}
                variant="confirmada"
              />
            </Box>

            <Box>
              <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                <FaInfoCircle size={20} color="var(--laranja)" />
                <Typography variant="h5" sx={{ fontWeight: 800, color: "var(--azulEscuro)" }}>
                  Fila de Espera / Pendentes
                </Typography>
              </Stack>
              <ReservationTable
                title="Todas as Datas"
                data={pendentes}
                variant="pendente"
              />
            </Box>
          </Stack>

        </Container>
      </Box >

      {/* =======================
          DIALOGS
      ======================= */}

      {/* DETALHES */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 900, color: "var(--azulEscuro)" }}>Detalhes da Reserva</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            <Typography><b>Cliente:</b> {reservaDetalhe?.primeiroNome} {reservaDetalhe?.sobrenome}</Typography>
            <Grid container>
              <Grid item xs={6}><Typography><b>Data:</b> {formatDateBR(reservaDetalhe?.data)}</Typography></Grid>
            </Grid>
            <Grid container>
              <Grid item xs={6}><Typography><b>Horário:</b> {reservaDetalhe?.horario}</Typography></Grid>
            </Grid>
            <Grid container>
              <Grid item xs={6}><Typography><b>Local:</b> {reservaDetalhe?.local}</Typography></Grid>
            </Grid>
            <Grid container>
              <Grid item xs={6}><Typography><b>Pessoas:</b> {reservaDetalhe?.pessoas}</Typography></Grid>
            </Grid>
            <Typography><b>Mesas:</b> {reservaDetalhe?.mesas?.join(", ") || "-"}</Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDetailsOpen(false)} variant="outlined" sx={{ borderRadius: 2, fontWeight: 700 }}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* EDITAR */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 900, color: "var(--azulEscuro)" }}>Editar Reserva</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack direction="row" spacing={2}>
              <TextField label="Nome" value={editForm.primeiroNome} onChange={(e) => onEditChange("primeiroNome", e.target.value)} fullWidth />
              <TextField label="Sobrenome" value={editForm.sobrenome} onChange={(e) => onEditChange("sobrenome", e.target.value)} fullWidth />
            </Stack>

            <TextField
              label="Telefone"
              value={editForm.telefone}
              onChange={(e) => {
                let v = e.target.value.replace(/\D/g, "");
                if (v.length > 0) {
                  v = v.replace(/^(\d{2})(\d)/g, "($1) $2");
                  v = v.replace(/(\d{5})(\d)/, "$1-$2");
                }
                onEditChange("telefone", v);
              }}
              inputProps={{ maxLength: 15 }}
              fullWidth
            />

            <Grid container spacing={2}>
              <Grid item xs={6} sx={{ position: "relative" }}>
                <FormControl fullWidth>
                  <InputLabel shrink>Data</InputLabel>

                  <OutlinedInput
                    readOnly
                    value={
                      editForm.data
                        ? format(parseDateLocal(editForm.data), "dd/MM/yyyy")
                        : "Selecione uma data"
                    }
                    onClick={() => setEditCalendarOpen(true)}
                    startAdornment={
                      <Box
                        component="span"
                        sx={{
                          mr: 1,
                          color: "text.secondary",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        📅
                      </Box>
                    }
                    sx={{
                      cursor: "pointer",
                      color: editForm.data ? "text.primary" : "text.secondary",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "rgba(0,0,0,0.23)",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "text.primary",
                      },
                    }}
                  />
                </FormControl>

                {editCalendarOpen && (
                  <Box
                    ref={editDatepickerRef}
                    sx={{
                      position: "absolute",
                      zIndex: 20,
                      mt: 1,
                      bgcolor: "background.paper",
                      boxShadow: 6,
                      borderRadius: 2,
                    }}
                  >
                    <DayPicker
                      mode="single"
                      locale={ptBR}
                      selected={parseDateLocal(editForm.data)}
                      onSelect={(d) => {
                        if (d) {
                          onEditChange("data", format(d, "yyyy-MM-dd"));
                          onEditChange("periodo", "");
                          onEditChange("horario", "");
                          setEditCalendarOpen(false);
                        }
                      }}
                      disabled={isDayDisabled}
                    />
                  </Box>
                )}
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth sx={{ minWidth: 256 }}>
                  <InputLabel>Período</InputLabel>
                  <Select value={editForm.periodo} label="Período" onChange={(e) => onEditChange("periodo", e.target.value)}>
                    <MenuItem value="almoco" disabled={!almocoDisponivelEdit}>Almoço</MenuItem>
                    <MenuItem value="jantar" disabled={!jantarDisponivelEdit}>Jantar</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <FormControl fullWidth sx={{ minWidth: 256 }} disabled={!editForm.periodo}>
                  <InputLabel>Horário</InputLabel>
                  <Select value={editForm.horario} label="Horário" onChange={(e) => onEditChange("horario", e.target.value)}>
                    {availableTimesEdit.map((t) => {
                      const dateObj = editForm.data ? new Date(editForm.data + "T00:00:00") : null;
                      const disabled = !isTimeAvailable(dateObj, editForm.periodo, t) || (capacidadeRestanteEdit !== null && Number(editForm.pessoas) > capacidadeRestanteEdit);
                      return <MenuItem key={t} value={t} disabled={disabled}>{t}</MenuItem>
                    })}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth sx={{ minHeight: 56 }}>
                  <InputLabel>Local</InputLabel>
                  <Select value={editForm.local} label="Local" onChange={(e) => onEditChange("local", e.target.value)}>
                    <MenuItem value="Salão">Salão</MenuItem>
                    <MenuItem value="Deck">Deck</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <FormControl fullWidth sx={{ minHeight: 56 }}>
              <InputLabel>Pessoas</InputLabel>
              <Select value={editForm.pessoas} label="Pessoas" onChange={(e) => onEditChange("pessoas", e.target.value)}>
                {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
                  <MenuItem key={n} value={n} disabled={capacidadeRestanteEdit !== null && n > capacidadeRestanteEdit}>{n}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <Divider textAlign="left"><Typography variant="caption" fontWeight={700}>SELEÇÃO DE MESAS</Typography></Divider>

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {(editForm.local === "Salão" ? [...Array(17)].map((_, i) => i + 1) : mesasDeck).map((mesa) => {
                const ocupada = editMesasOcupadas.includes(mesa);
                const selected = editMesasSelecionadas.includes(mesa);
                return (
                  <Button
                    key={mesa}
                    variant={selected ? "contained" : "outlined"}
                    disabled={ocupada}
                    onClick={() => toggleEditMesa(mesa)}
                    size="small"
                    sx={{ borderRadius: 2, minWidth: 40, borderColor: selected ? "primary.main" : "#e0e0e0", color: selected ? "#fff" : "text.secondary", bgcolor: selected ? "var(--azulEscuro)" : "transparent", "&:hover": { bgcolor: selected ? "var(--azulEscuro)" : "#f5f5f5" } }}
                  >
                    {mesa}
                  </Button>
                );
              })}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setEditOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={salvarEdicaoReserva} sx={{ bgcolor: "var(--azulEscuro)", borderRadius: 2 }}>Salvar</Button>
        </DialogActions>
      </Dialog>

      {/* DIALOG MESAS (CONFIRMACAO) */}
      <Dialog open={dialogOpen} onClose={fecharDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 900 }}>Selecionar Mesas</DialogTitle>
        <DialogContent dividers>
          <Typography gutterBottom>Reserva para <b>{reservaSelecionada?.pessoas} pessoas</b> no <b>{reservaSelecionada?.local}</b>.</Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 2 }}>
            {(reservaSelecionada?.local === "Salão" ? [...Array(17)].map((_, i) => i + 1) : mesasDeck).map((mesa) => {
              const ocupada = mesasOcupadas.includes(mesa);
              const selected = mesasSelecionadas.includes(mesa);
              return (
                <Button key={mesa} variant={selected ? "contained" : "outlined"} disabled={ocupada} onClick={() => toggleMesa(mesa)} sx={{ borderRadius: 2, minWidth: 45, bgcolor: selected ? "var(--azulEscuro)" : "transparent", color: selected ? "#fff" : "inherit" }}>
                  {mesa}
                </Button>
              )
            })}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={fecharDialog}>Cancelar</Button>
          <Button variant="contained" onClick={() => { if (mesasSelecionadas.length === 0) return showSnack("Selecione uma mesa"); setConfirmPagamentoOpen(true); }} sx={{ bgcolor: "var(--azulEscuro)", borderRadius: 2 }}>Confirmar</Button>
        </DialogActions>
      </Dialog>

      {/* DIALOG PAGAMENTO */}
      <Dialog open={confirmPagamentoOpen} onClose={() => setConfirmPagamentoOpen(false)}>
        <DialogTitle>Pagamento Realizado?</DialogTitle>
        <DialogContent><Typography>O cliente já efetuou o pagamento do sinal?</Typography></DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setConfirmPagamentoOpen(false)}>Não</Button>
          <Button variant="contained" onClick={async () => { await confirmarAlocacaoMesas(); setConfirmPagamentoOpen(false); setDialogOpen(false); }} sx={{ bgcolor: "var(--azulEscuro)" }}>Sim, Confirmar</Button>
        </DialogActions>
      </Dialog>

      {/* DIALOG CANCELAMENTO */}
      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)}>
        <DialogTitle sx={{ color: "var(--vermelho)" }}>Cancelar Reserva?</DialogTitle>
        <DialogContent><Typography>Essa ação liberará as mesas e a capacidade.</Typography></DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setCancelDialogOpen(false)}>Voltar</Button>
          <Button variant="contained" color="error" onClick={async () => { await cancelarReserva(reservaParaCancelar); setCancelDialogOpen(false); }} sx={{ borderRadius: 2 }}>Cancelar Reserva</Button>
        </DialogActions>
      </Dialog>

      {/* SNACKBAR */}
      <Snackbar open={snackOpen} autoHideDuration={3000} onClose={() => setSnackOpen(false)} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert onClose={() => setSnackOpen(false)} severity="warning" variant="filled" sx={{ width: "100%", fontWeight: 700 }}>{snackMsg}</Alert>
      </Snackbar>
    </>
  );
};

export default Dashboard;